import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, API } from "../lib/api";
import { PageHeader, Card, StatTile, Badge, EmptyState, Btn } from "../components/kr";
import Barcode from "../components/Barcode";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, CheckSquare, Square, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Pagination, usePagination } from "../components/Pagination";

export default function Barcodes() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("in_stock");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [expandedMaterials, setExpandedMaterials] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get("/barcodes/summary");
      setD(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load barcodes");
    }
  }, []);
  
  useEffect(() => { load(); }, [load]);
  const { page, setPage, pageSize, setPageSize } = usePagination(10);
  useEffect(() => { setPage(1); }, [q, statusFilter, setPage]);

  // Filter items safely checking if data exists
  const filtered = useMemo(() => {
    if (!d || !d.items) return [];
    return d.items.filter(i => {
      const matchesSearch = !q || i.barcode.toLowerCase().includes(q.toLowerCase()) || i.raw_material_name.toLowerCase().includes(q.toLowerCase());
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "consumed" && i.status === "consumed") ||
        (statusFilter === "in_stock" && i.status === "in_stock") ||
        (statusFilter === "pending_receipt" && i.status === "pending_receipt");
      
      return matchesSearch && matchesStatus;
    });
  }, [d, q, statusFilter]);

  // Group items by Raw Material Name
  const groupedMaterials = useMemo(() => {
    const groups = {};
    filtered.forEach(item => {
      const name = item.raw_material_name;
      if (!groups[name]) groups[name] = [];
      groups[name].push(item);
    });
    return groups;
  }, [filtered]);

  const materialNames = Object.keys(groupedMaterials);
  const pagedMaterialNames = materialNames.slice((page - 1) * pageSize, page * pageSize);

  const selectedCodes = Object.keys(selected).filter(k => selected[k]);
  
  const toggleSelection = (bc) => setSelected(s => ({ ...s, [bc]: !s[bc] }));
  
  const selectAllVisible = () => {
    const next = { ...selected };
    filtered.forEach(i => { next[i.barcode] = true; });
    setSelected(next);
  };

  // Toggle all barcodes belonging to a specific raw material group
  const toggleMaterialGroupSelection = (e, materialName, groupItems) => {
    e.stopPropagation(); // Prevent accordion from expanding/collapsing when clicking checkbox
    
    const allGroupBarcodes = groupItems.map(i => i.barcode);
    const isAllSelected = allGroupBarcodes.every(bc => !!selected[bc]);

    setSelected(prev => {
      const next = { ...prev };
      allGroupBarcodes.forEach(bc => {
        next[bc] = !isAllSelected;
      });
      return next;
    });
  };
  
  const clearSelection = () => setSelected({});
  const toggleMaterialExpand = (name) => setExpandedMaterials(prev => ({ ...prev, [name]: !prev[name] }));

  const downloadPng = async (barcode) => {
    try {
      const res = await fetch(`${API}/barcodes/${encodeURIComponent(barcode)}/png`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${barcode}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      toast.error(e.message || "PNG download failed");
    }
  };

  const downloadPdf = async () => {
    if (selectedCodes.length === 0) { toast.error("Select at least one barcode"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/barcodes/export.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ barcodes: selectedCodes, columns: 3 }),
      });
      if (!res.ok) throw new Error("PDF export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `barcodes-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success(`Exported ${selectedCodes.length} label(s)`);
    } catch (e) {
      toast.error(e.message || "PDF export failed");
    } finally { setBusy(false); }
  };

  const downloadXlsx = async () => {
    const selected = selectedCodes.length;
    try {
      const res = await fetch(`${API}/barcodes/export.xlsx`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ barcodes: selected > 0 ? selectedCodes : [] }),
      });
      if (!res.ok) throw new Error(`Excel export failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = selected > 0
        ? `barcodes-selected-${selected}-${new Date().toISOString().slice(0, 10)}.xlsx`
        : `barcodes-all-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success(
        selected > 0
          ? `Exported ${selected} selected barcode${selected === 1 ? "" : "s"}`
          : "Exported all barcodes"
      );
    } catch (e) {
      toast.error(e.message || "Excel export failed");
    }
  };

  if (!d) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="barcodes-page">
      <PageHeader label="TRACKING" title="Barcodes Overview" subtitle="Every raw-material bag ever registered, its status, and where it's been used." />

      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatTile testId="bc-total" label="Total Barcodes" value={d.total} />
        <StatTile testId="bc-instock" label="In Stock" value={d.in_stock} accent="text-green-700" />
        <StatTile testId="bc-consumed" label="Consumed" value={d.consumed} accent="text-zinc-600" />
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <input
          placeholder="Search barcode or material..."
          value={q}
          onChange={e => setQ(e.target.value)}
          className="flex-1 min-w-[200px] max-w-sm px-3 py-2 border border-zinc-200 text-sm font-mono rounded focus:outline-none focus:border-orange-500 transition-colors"
          data-testid="bc-search"
        />

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-zinc-200 bg-white text-sm rounded focus:outline-none focus:border-orange-500 h-[38px] cursor-pointer transition-colors"
        >
          <option value="all">All Statuses</option>
          <option value="in_stock">In Stock</option>
          <option value="pending_receipt">Pending Receipt</option>
          <option value="consumed">Consumed / Used</option>
        </select>

        <Btn type="button" variant="secondary" onClick={selectAllVisible} data-testid="bc-select-all">
          <CheckSquare size={12} /> Select Filtered ({filtered.length})
        </Btn>
        
        {selectedCodes.length > 0 && (
          <>
            <Btn type="button" variant="secondary" onClick={clearSelection}><Square size={12} /> Clear</Btn>
            <Btn type="button" onClick={downloadPdf} disabled={busy} data-testid="bc-export-pdf">
              <FilePdf size={12} /> {busy ? "Exporting…" : `Export ${selectedCodes.length} as PDF`}
            </Btn>
          </>
        )}
        <Btn type="button" variant="secondary" data-testid="bc-export-xlsx"
             onClick={downloadXlsx}>
          <DownloadSimple size={12} />
          {selectedCodes.length > 0
            ? `Export ${selectedCodes.length} selected as Excel`
            : "Export all as Excel"}
        </Btn>
      </div>

      {materialNames.length === 0 ? <EmptyState title="No barcodes match your filters" /> : (
        <>
          <div className="space-y-3">
            {pagedMaterialNames.map(materialName => {
              const barcodes = groupedMaterials[materialName];
              const isExpanded = !!expandedMaterials[materialName];
              const allGroupBarcodes = barcodes.map(i => i.barcode);
              const isAllGroupSelected = allGroupBarcodes.every(bc => !!selected[bc]);
              const isSomeGroupSelected = allGroupBarcodes.some(bc => !!selected[bc]) && !isAllGroupSelected;

              return (
                <div key={materialName} className="border border-zinc-200 bg-zinc-50/50 rounded-md overflow-hidden transition-all shadow-sm">
                  {/* Aggregated Banner Row */}
                  <div
                    onClick={() => toggleMaterialExpand(materialName)}
                    className="w-full px-4 py-3.5 flex items-center justify-between bg-white border-b border-zinc-100 hover:bg-zinc-50/80 transition-colors text-left cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => toggleMaterialGroupSelection(e, materialName, barcodes)}
                        className="text-orange-600 hover:text-orange-700 p-0.5 rounded transition-colors"
                        title="Toggle selection for all bags in this group"
                      >
                        {isAllGroupSelected ? (
                          <CheckSquare size={20} weight="fill" />
                        ) : isSomeGroupSelected ? (
                          <CheckSquare size={20} className="opacity-70" />
                        ) : (
                          <Square size={20} className="text-zinc-400 hover:text-zinc-600" />
                        )}
                      </button>

                      <div className="flex items-center gap-2">
                        {isExpanded ? <CaretDown size={16} className="text-zinc-400" /> : <CaretRight size={16} className="text-zinc-400" />}
                        <span className="font-bold text-zinc-800 text-base tracking-tight">{materialName}</span>
                      </div>
                    </div>

                    <div className="text-xs text-zinc-500 font-mono bg-zinc-100 px-2.5 py-1 rounded-md font-semibold">
                      {barcodes.length} batch{barcodes.length > 1 ? "es" : ""}
                    </div>
                  </div>

                  {/* Enhanced Responsive Grid Layout */}
                  {isExpanded && (
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 bg-zinc-50/50">
                      {barcodes.map(i => {
                        const isSel = !!selected[i.barcode];
                        return (
                          <Card 
                            key={i.id} 
                            className={`!p-3 bg-white border border-zinc-100 rounded-md relative group transition-all duration-200 hover:shadow-md ${
                              isSel ? "ring-2 ring-orange-500 border-transparent shadow-sm" : "hover:border-zinc-300"
                            }`} 
                            data-testid={`bc-${i.barcode}`}
                          >
                            {/* Card Header Layer */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex items-start gap-2">
                                <button
                                  type="button"
                                  onClick={() => toggleSelection(i.barcode)}
                                  className="mt-0.5 text-orange-600 hover:text-orange-700 flex-shrink-0"
                                  data-testid={`bc-select-${i.barcode}`}
                                >
                                  {isSel ? <CheckSquare size={16} weight="fill" /> : <Square size={16} className="text-zinc-400" />}
                                </button>
                                <div>
                                  <div className="font-mono text-xs font-bold text-zinc-800 tracking-tight break-all">{i.barcode}</div>
                                  <div className="text-[10px] font-mono text-zinc-400 mt-0.5 flex flex-wrap gap-x-1.5 gap-y-0.5">
                                    <span>{i.received_at?.slice(0, 10)}</span>
                                    <span className="text-zinc-300">•</span>
                                    <span className="text-zinc-600 font-medium">{i.weight}{i.unit}</span>
                                    {i.bag_index && i.bag_total > 1 && (
                                      <>
                                        <span className="text-zinc-300">•</span>
                                        <span className="bg-zinc-100 text-zinc-600 px-1 rounded-sm">Bag {i.bag_index}/{i.bag_total}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Badge tone={i.status === "in_stock" ? "green" : i.status === "pending_receipt" ? "orange" : "zinc"} className="text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-semibold">
                                {i.status.replace("_", " ")}
                              </Badge>
                            </div>

                            {/* Minimal Info Row */}
                            <div className="text-[11px] font-mono space-y-0.5 border-t border-zinc-100 pt-2">
                              <div className="flex justify-between">
                                <span className="text-zinc-400">Remaining:</span>
                                <span className="font-bold text-zinc-700">{i.remaining_weight}{i.unit}</span>
                              </div>
                              {i.unit_cost > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-zinc-400">Cost:</span>
                                  <span className="text-zinc-600">₹{i.unit_cost}/{i.unit}</span>
                                </div>
                              )}
                              {i.linked_po_no && (
                                <div className="flex justify-between">
                                  <span className="text-zinc-400">PO Ref:</span>
                                  <span className="text-orange-700 font-medium">{i.linked_po_no}</span>
                                </div>
                              )}
                            </div>

                            {/* Conditional Minimal Usage Info */}
                            {i.usage?.length > 0 && (
                              <div className="mt-1.5 pt-1.5 border-t border-zinc-100/70 text-[10px]">
                                <span className="text-zinc-400 font-mono">Used In: </span>
                                <span className="font-mono text-zinc-600">
                                  {i.usage.map(u => `${u.batch_no} (${u.weight}${i.unit})`).join(", ")}
                                </span>
                              </div>
                            )}

                            {/* Permanent Visibility Footer Bar (Hover opacity overrides removed) */}
                            <div className="mt-2.5 pt-2 border-t border-zinc-100 flex items-center justify-between dynamic-footer">
                              <div className="flex items-center scale-75 origin-left opacity-80">
                                <Barcode value={i.barcode} height={18} displayValue={false} />
                              </div>
                              <button
                                type="button"
                                onClick={() => downloadPng(i.barcode)}
                                className="text-[10px] text-orange-600 hover:text-orange-800 font-bold tracking-wider flex items-center gap-1 bg-orange-50 hover:bg-orange-100/80 px-2 py-1 rounded transition-colors"
                                data-testid={`bc-download-${i.barcode}`}
                              >
                                <DownloadSimple size={10} /> PNG
                              </button>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={materialNames.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="bc-pager"
            className="border-0 bg-transparent px-0 mt-4"
          />
        </>
      )}
    </div>
  );
}